import type { FC } from 'react';
import { Checkbox, Col, Row } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import EmptyView from '@/shared/components/layouts/views/EmptySection';
import CompactVirtualizedList from '@/shared/components/lists/CompactVirtualizedList';
import TaskParticipantAvatarGroup from '@/shared/components/tasks/TaskParticipantAvatarGroup';
import TaskStatusIcon from '@/shared/components/tasks/TaskStatusIcon';
import { MiddleDot } from '@/shared/components/typography';
import { WorkspaceResourceGlyph } from '@/shared/components/workspace-resources';
import { ellipsisStyling, formatDate } from '@/utilities';

import { isLifecycleTaskCompleted } from '../progress';
import type { LifecycleTaskPreview } from '../types';

interface LifecycleTaskListProps {
    emptyDescription?: string;
    emptyHeight?: string;
    emptyLabel?: string;
    tasks: LifecycleTaskPreview[];
}

const LifecycleTaskList: FC<LifecycleTaskListProps> = ({
    emptyDescription = 'Tasks will appear here when they are added to this lifecycle.',
    emptyHeight = '8rem',
    emptyLabel = 'No Lifecycle Tasks',
    tasks,
}) => {
    const { token } = useAntDesignTheme();

    if (!tasks.length) {
        return (
            <EmptyView
                description={emptyDescription}
                height={emptyHeight}
                icon={<WorkspaceResourceGlyph resource='task' size='1rem' />}
                label={emptyLabel}
            />
        );
    }

    return (
        <CompactVirtualizedList
            animated={false}
            collapsible={false}
            dataSource={tasks}
            estimateSize={64}
            listHeight={Math.max(64, tasks.length * 64)}
            rowKey='id'
            showHeader={false}
            title='Lifecycle Tasks'
            renderItem={(task) => {
                const completed = isLifecycleTaskCompleted(task);

                return (
                    <CompactVirtualizedList.Item
                        data-lifecycle-task-id={task.id}
                        hoverable={false}
                        style={{ minHeight: 64, padding: '0.5rem 0.75rem' }}
                    >
                        <Col flex='auto' style={{ minWidth: 0 }}>
                            <Row align='middle' justify='space-between' wrap={false}>
                                <Col flex='auto' style={{ minWidth: 0 }}>
                                    <span style={{ alignItems: 'center', display: 'flex', gap: '0.5rem', minWidth: 0 }}>
                                        <Checkbox
                                            aria-label={`${completed ? 'Completed' : 'Incomplete'} task: ${task.title}`}
                                            checked={completed}
                                            style={{ flex: 'none', pointerEvents: 'none' }}
                                        />
                                        <span
                                            style={{
                                                color: completed ? token.colorTextTertiary : token.colorText,
                                                textDecoration: completed ? 'line-through' : undefined,
                                                ...ellipsisStyling,
                                            }}
                                        >
                                            {task.title}
                                        </span>
                                    </span>
                                </Col>
                                {task.status ? (
                                    <Col
                                        flex='none'
                                        style={{ alignItems: 'center', display: 'inline-flex', gap: '0.375rem' }}
                                    >
                                        <TaskStatusIcon status={task.status} />
                                        <small style={{ color: token.colorTextTertiary }}>{task.status.name}</small>
                                    </Col>
                                ) : null}
                            </Row>
                            <Row align='middle' justify='space-between' style={{ marginTop: '0.375rem' }} wrap={false}>
                                <Col flex='auto' style={{ minWidth: 0 }}>
                                    <span style={{ alignItems: 'center', display: 'inline-flex', minHeight: 18 }}>
                                        {task.assignees?.length ? (
                                            <span aria-label={`${task.assignees.length} assignees`}>
                                                <TaskParticipantAvatarGroup participants={task.assignees} size={16} />
                                            </span>
                                        ) : null}
                                        {task.assignees?.length && task.reviewers?.length ? (
                                            <MiddleDot horizontalMargin='0.375rem' />
                                        ) : null}
                                        {task.reviewers?.length ? (
                                            <span aria-label={`${task.reviewers.length} reviewers`}>
                                                <TaskParticipantAvatarGroup participants={task.reviewers} size={16} />
                                            </span>
                                        ) : null}
                                    </span>
                                </Col>
                                {task.updatedAt ? (
                                    <Col flex='none'>
                                        <small style={{ color: token.colorTextTertiary }}>
                                            Last updated {formatDate(new Date(task.updatedAt), 'mini')}
                                        </small>
                                    </Col>
                                ) : null}
                            </Row>
                        </Col>
                    </CompactVirtualizedList.Item>
                );
            }}
        />
    );
};

export default LifecycleTaskList;
